import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FolderOpen,
  FileText,
  ExternalLink,
  ChevronLeft,
  Trash2,
  Loader2,
  X,
  Home,
  Pencil,
  Check,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listItems,
  listFolders,
  patchItem,
  updateFolder,
  renameItem,
  deleteFolder,
  ApiError,
} from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { IhubItem, Folder } from "@/lib/types";
import { useSession } from "@/hooks/use-session";

/**
 * Build a Drive open-URL that FORCES the correct Google account.
 *
 * Background: Chrome supports multiple Google accounts. drive.google.com/
 * file/d/<id>/view opens with whichever account is currently "active" in
 * Chrome's Drive tab — NOT necessarily the account that owns the file.
 * If the user has, say, panthuv@gmail.com active and the file is in
 * s0123uma@gmail.com's Drive, they get "File not found".
 *
 * Adding ?usp=drive_web&authuser=<email> tells Drive which account the
 * link is for; Drive then either uses it (if signed in) or prompts a
 * one-click account switch. Either way the user lands on the right file.
 */
function buildDriveUrl(fileId: string, authEmail: string | null): string {
  const base = `https://drive.google.com/file/d/${fileId}/view`;
  if (!authEmail) return base;
  return `${base}?usp=drive_web&authuser=${encodeURIComponent(authEmail)}`;
}

export const Route = createFileRoute("/_app/library")({
  component: LibraryView,
});

interface FolderBucket {
  name: string;
  items: IhubItem[];
}

/**
 * Library — folder grid drilling into filed documents.
 *
 * Two-level navigation:
 *   - Top level: grid of all folders containing at least one FILED
 *     doc + every user-defined top-level folder (so freshly-created
 *     empty folders still appear).
 *   - Drilled in: list of docs in the picked folder, each linking
 *     out to Drive via drive_file_id.
 *
 * The folder list is the UNION of:
 *   - listFolders() (everything the user has defined)
 *   - chosen_folder values present in the FILED list (catches AI-
 *     suggested-then-confirmed folders that processConfirm seeded
 *     after the user's initial folder tree).
 */
function LibraryView() {
  const filedQuery = useQuery({
    queryKey: queryKeys.ihub("filed"),
    queryFn: () => listItems("filed"),
  });
  const foldersQuery = useQuery({
    queryKey: queryKeys.folders,
    queryFn: listFolders,
  });

  const [drillFolder, setDrillFolder] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bulk-delete 2-tap inline confirmation (window.confirm is broken
  // in iOS PWA standalone mode — same fix we made for iHub).
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);
  // "Also delete from Drive" opt-in — checkbox sits inside the bulk
  // confirm strip. Default false (safe: app record gone, Drive copy
  // stays). When true, server PATCHes each Drive file { trashed: true }
  // after the app delete commits.
  const [bulkDeleteDrive, setBulkDeleteDrive] = useState(false);
  // Inline folder rename state for the drill-in header. When the user
  // taps the pencil button, we show an Input pre-filled with the leaf
  // name. Save → PATCH /api/folders/:id, which now cascades into
  // ihub_items.chosen_folder so docs stay visible under the new name.
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [folderRenameDraft, setFolderRenameDraft] = useState("");
  const qc = useQueryClient();

  // Clear selection when leaving the drilled-in folder view.
  useEffect(() => {
    if (drillFolder === null) setSelected(new Set());
  }, [drillFolder]);

  // Reset the bulk-delete confirm state when the selection clears (or
  // the drill-in is exited) so the toolbar can't get stuck in confirm
  // mode after items disappear.
  useEffect(() => {
    if (selected.size === 0 && confirmingBulkDelete) {
      setConfirmingBulkDelete(false);
    }
  }, [selected.size, confirmingBulkDelete]);

  const toggleSelect = (file_id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(file_id)) next.delete(file_id);
      else next.add(file_id);
      return next;
    });
  };

  const buckets = useMemo<FolderBucket[]>(() => {
    const map = new Map<string, IhubItem[]>();
    for (const f of foldersQuery.data ?? []) {
      if (f.parent_id === null) map.set(f.name, []);
    }
    for (const item of filedQuery.data?.items ?? []) {
      const name = item.chosen_folder || "(unfiled)";
      if (!map.has(name)) map.set(name, []);
      map.get(name)!.push(item);
    }
    const definedNames = new Set(
      (foldersQuery.data ?? [])
        .filter((f) => f.parent_id === null)
        .map((f) => f.name),
    );
    return [...map.entries()]
      .filter(
        ([name, items]) => items.length > 0 || definedNames.has(name),
      )
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, items]) => ({ name, items }));
  }, [filedQuery.data, foldersQuery.data]);

  const totalFiled = filedQuery.data?.items.length ?? 0;

  if (drillFolder !== null) {
    const bucket = buckets.find((b) => b.name === drillFolder);
    const bucketItems = bucket?.items ?? [];
    const allSelected =
      bucketItems.length > 0 && selected.size === bucketItems.length;
    const selectAll = () =>
      setSelected(new Set(bucketItems.map((i) => i.file_id)));
    const clearSelection = () => setSelected(new Set());

    // Bulk delete commit. window.confirm is unreliable in iOS PWA
    // standalone — same problem we hit on iHub. The two-tap inline
    // toolbar pattern below guards intent without a native dialog.
    // Toasts replace the old alert() so the user gets non-blocking
    // feedback and the page state stays interactive.
    //
    // Auto-exit: if the bulk delete empties the bucket (no remaining
    // items in this folder), navigate back to the Library grid so
    // the user isn't stranded on an empty drill-in.
    const bulkDeleteCommit = async () => {
      if (selected.size === 0 || bulkBusy) return;
      const ids = [...selected];
      const alsoDrive = bulkDeleteDrive;
      setConfirmingBulkDelete(false);
      setBulkDeleteDrive(false);
      setBulkBusy(true);
      const byId = new Map(bucketItems.map((i) => [i.file_id, i]));
      const results = await Promise.allSettled(
        ids.map(async (id) => {
          const it = byId.get(id);
          if (!it) throw new Error("item not in folder");
          await patchItem(id, {
            event: "USER_DELETE",
            expected_version: it.version,
            delete_drive_copy: alsoDrive,
          });
        }),
      );
      const fails = results.filter((r) => r.status === "rejected").length;
      const successes = ids.length - fails;
      setBulkBusy(false);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["ihub"] });
      // Auto-exit when the folder is empty after this delete.
      if (successes === bucketItems.length) {
        setDrillFolder(null);
      }
      if (fails === 0) {
        toast.success(
          `Deleted ${successes} ${successes === 1 ? "doc" : "docs"} from "${drillFolder}"`,
          {
            description:
              "The Drive copy stays — delete it manually if you want it gone there too.",
          },
        );
      } else if (fails === ids.length) {
        toast.error(`Couldn't delete ${fails} ${fails === 1 ? "doc" : "docs"}`, {
          description: "Try again — it may be mid-Drive-upload.",
        });
      } else {
        toast.warning(`Deleted ${successes} of ${ids.length}`, {
          description: `${fails} couldn't be removed; retry shortly.`,
        });
      }
    };

    // Resolve the current drillFolder (which is a path string like
    // "Bills / Electricity" or "Donations") to its leaf user_folders
    // row id so we can PATCH it. Top-level rows match by name; nested
    // rows match by parent + leaf name.
    const currentFolderId = (() => {
      const folders = foldersQuery.data ?? [];
      const segs = drillFolder.split(" / ");
      if (segs.length === 1) {
        return folders.find(
          (f) => f.parent_id === null && f.name === segs[0],
        )?.id ?? null;
      }
      const parent = folders.find(
        (f) => f.parent_id === null && f.name === segs[0],
      );
      if (!parent) return null;
      return (
        folders.find(
          (f) => f.parent_id === parent.id && f.name === segs[segs.length - 1],
        )?.id ?? null
      );
    })();

    // Flat list of folder paths (Top-level + "Parent / Child")
    // for the "Move all to…" Select dropdown.
    const allFolderPaths = (() => {
      const folders = foldersQuery.data ?? [];
      const byId = new Map<number, string>();
      for (const f of folders) byId.set(f.id, f.name);
      const out: { id: number; path: string }[] = [];
      for (const f of folders) {
        const path =
          f.parent_id === null
            ? f.name
            : `${byId.get(f.parent_id) ?? "?"} / ${f.name}`;
        out.push({ id: f.id, path });
      }
      return out.sort((a, b) => a.path.localeCompare(b.path));
    })();

    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-8 py-8">
        <header className="flex items-center gap-2 mb-3 flex-wrap">
          {/* Back to library root (folder grid) */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDrillFolder(null)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Library
          </Button>
          {/* Back to home (iHub inbox). Especially useful after a
              user taps "Open in Drive" → that opens a new tab; the
              app tab they came from might be hard to find. This
              button gives them a single tap back to the home view
              without going through the Library grid first. */}
          <Button
            variant="outline"
            size="sm"
            asChild
            title="Back to iHub home"
          >
            <Link to="/ihub">
              <Home className="h-4 w-4 mr-1" />
              Home
            </Link>
          </Button>
          {/* Folder name + inline rename. When not renaming, show the
              name + a pencil button. When renaming, show an Input +
              Save/Cancel. Save calls updateFolder which now cascades
              the rename into ihub_items.chosen_folder so the renamed
              folder still shows the same docs. */}
          {renamingFolder ? (
            <div className="flex items-center gap-2 ml-1 flex-1 min-w-[12rem]">
              <Input
                value={folderRenameDraft}
                onChange={(e) => setFolderRenameDraft(e.target.value)}
                placeholder="New folder name"
                className="font-display text-lg h-10"
                autoFocus
              />
              <Button
                size="sm"
                disabled={
                  !currentFolderId ||
                  !folderRenameDraft.trim() ||
                  folderRenameDraft.trim() === drillFolder.split(" / ").pop()
                }
                onClick={async () => {
                  if (!currentFolderId) return;
                  try {
                    const updated = await updateFolder(currentFolderId, {
                      name: folderRenameDraft.trim(),
                    });
                    const segs = drillFolder.split(" / ");
                    segs[segs.length - 1] = updated.name;
                    const newPath = segs.join(" / ");
                    setDrillFolder(newPath);
                    setRenamingFolder(false);
                    setFolderRenameDraft("");
                    toast.success(`Renamed to "${updated.name}"`, {
                      description:
                        "All filed docs now show the new name. Drive folder name is unchanged for past uploads — rename it manually in Drive if you want them to match.",
                    });
                    qc.invalidateQueries({ queryKey: queryKeys.folders });
                    qc.invalidateQueries({ queryKey: ["ihub"] });
                  } catch (e) {
                    toast.error("Rename failed", {
                      description:
                        e instanceof Error ? e.message : "Try again",
                    });
                  }
                }}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setRenamingFolder(false);
                  setFolderRenameDraft("");
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl ml-1">{drillFolder}</h1>
              <Button
                size="sm"
                variant="ghost"
                disabled={!currentFolderId}
                onClick={() => {
                  setFolderRenameDraft(drillFolder.split(" / ").pop() ?? "");
                  setRenamingFolder(true);
                }}
                title={
                  currentFolderId
                    ? "Rename this folder"
                    : "This folder isn't formalised in your library yet — confirm a doc here first"
                }
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          <span className="text-sm text-muted-foreground ml-auto">
            {bucketItems.length} item
            {bucketItems.length === 1 ? "" : "s"}
          </span>
        </header>

        {/* Move-to-different-folder picker. Shown beneath the header
            as a secondary action — pick a folder, jump to it. (Doesn't
            move the underlying docs; just navigates. Use Rename if you
            want to change THIS folder's name, or Delete on individual
            docs to remove them and re-file via iHub.) */}
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          <label className="text-xs text-muted-foreground">Jump to:</label>
          <Select
            value={drillFolder}
            onValueChange={(v) => v && v !== drillFolder && setDrillFolder(v)}
          >
            <SelectTrigger className="h-8 w-auto min-w-[10rem] text-xs">
              <SelectValue placeholder="Pick a folder…" />
            </SelectTrigger>
            <SelectContent>
              {allFolderPaths.map((fp) => (
                <SelectItem key={fp.id} value={fp.path}>
                  {fp.path}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk selection toolbar — same 2-tap inline confirm pattern
            as the iHub bulk toolbar. window.confirm is unreliable in
            iOS PWA standalone mode (the dialog is silently dropped),
            so we morph the toolbar in place: tap "Delete N" → toolbar
            shows "Delete N? Yes, delete · Cancel"; tap Yes commits. */}
        {selected.size > 0 && (
          <div className="sticky top-14 z-20 -mx-4 sm:-mx-8 mb-4 px-4 sm:px-8 py-2.5 bg-primary text-primary-foreground shadow-md flex items-center gap-2 flex-wrap">
            {confirmingBulkDelete ? (
              <>
                <span className="text-sm font-medium">
                  Delete {selected.size}{" "}
                  {selected.size === 1 ? "doc" : "docs"} from "
                  {drillFolder}"?
                </span>
                {/* Also delete from Drive — opt-in. Toggling sends
                    delete_drive_copy:true on each USER_DELETE so the
                    server PATCHes the Drive file { trashed: true }
                    after the app delete commits. Drive trash is
                    restorable for 30 days; this is the closest thing
                    to "Delete everywhere". */}
                <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <Checkbox
                    checked={bulkDeleteDrive}
                    onCheckedChange={(v) =>
                      setBulkDeleteDrive(v === true)
                    }
                    aria-label="Also delete from Drive"
                    className="h-4 w-4 border-primary-foreground/60 data-[state=checked]:bg-primary-foreground data-[state=checked]:text-primary"
                  />
                  Also delete from Drive
                </label>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={bulkBusy}
                  onClick={bulkDeleteCommit}
                  className="h-8"
                >
                  {bulkBusy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Yes, delete
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkBusy}
                  onClick={() => setConfirmingBulkDelete(false)}
                  className="text-primary-foreground hover:bg-primary-foreground/10 h-8"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <span className="text-sm font-medium">
                  {selected.size} selected
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={allSelected ? clearSelection : selectAll}
                  className="text-xs h-8"
                >
                  {allSelected ? "Clear" : "Select all"}
                </Button>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={bulkBusy}
                  onClick={() => setConfirmingBulkDelete(true)}
                  className="h-8"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete {selected.size}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearSelection}
                  className="text-primary-foreground hover:bg-primary-foreground/10 h-8 w-8 p-0"
                  aria-label="Clear selection"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        )}

        {/* Empty-selection helper: "Select all" affordance as a real
            outline button (not flat text) so it reads as clickable. */}
        {bucketItems.length > 1 && selected.size === 0 && (
          <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAll}
              className="h-9 border-primary/40 text-foreground hover:bg-primary/10 hover:border-primary/60 font-medium"
            >
              <span className="inline-block w-4 h-4 mr-2 rounded border-2 border-current" />
              Select all {bucketItems.length}
            </Button>
            <span className="text-xs text-muted-foreground">
              or tap the file icon on a row
            </span>
          </div>
        )}

        {!bucket || bucketItems.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p>No documents filed under this folder yet.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {bucketItems.map((item) => (
              <FiledRow
                key={item.file_id}
                item={item}
                selected={selected.has(item.file_id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-8 py-8">
      <header className="flex items-baseline justify-between mb-6">
        <h1 className="font-display text-3xl">Library</h1>
        <span className="text-sm text-muted-foreground">
          {filedQuery.isLoading
            ? "Loading…"
            : `${totalFiled} document${totalFiled === 1 ? "" : "s"} filed`}
        </span>
      </header>

      {filedQuery.isLoading || foldersQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : buckets.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <p>
            No filed documents yet. Confirm a doc on the iHub to see it
            here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {buckets.map((b) => (
            <FolderTile
              key={b.name}
              name={b.name}
              docCount={b.items.length}
              docs={b.items}
              folders={foldersQuery.data ?? []}
              onOpen={() => setDrillFolder(b.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// FolderTile — folder card on the Library grid with inline delete
// ──────────────────────────────────────────────────────────────────────

function FolderTile({
  name,
  docCount,
  docs,
  folders,
  onOpen,
}: {
  name: string;
  docCount: number;
  docs: IhubItem[];
  folders: Folder[];
  onOpen: () => void;
}) {
  const qc = useQueryClient();
  // Two-tap inline delete: first tap → strip morphs to confirm row;
  // second tap → DELETE /api/folders/:id (+ optional doc deletion
  // when the folder still has docs and the user checked the box).
  const [confirming, setConfirming] = useState(false);
  const [alsoDocs, setAlsoDocs] = useState(false);
  const [busy, setBusy] = useState(false);

  // Auto-collapse the confirm strip after 6s of no decision.
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 6000);
    return () => clearTimeout(t);
  }, [confirming]);

  // Resolve the folder's user_folders row id from the name.
  const folderRow = (() => {
    const segs = name.split(" / ");
    if (segs.length === 1) {
      return folders.find(
        (f) => f.parent_id === null && f.name === segs[0],
      );
    }
    const parent = folders.find(
      (f) => f.parent_id === null && f.name === segs[0],
    );
    if (!parent) return undefined;
    return folders.find(
      (f) => f.parent_id === parent.id && f.name === segs[segs.length - 1],
    );
  })();

  const handleDelete = async () => {
    if (!folderRow) {
      toast.error("Folder isn't formalised", {
        description:
          "Confirm a doc into this folder first, then it can be renamed/deleted.",
      });
      return;
    }
    setBusy(true);
    try {
      // If user opted into deleting docs too, delete them FIRST so the
      // folder is empty when we DELETE the folders row (avoids the
      // server's safety check, if any). Sequential to keep toasts
      // accurate; small N so latency is fine.
      let docFails = 0;
      if (alsoDocs && docs.length > 0) {
        const results = await Promise.allSettled(
          docs.map((d) =>
            patchItem(d.file_id, {
              event: "USER_DELETE",
              expected_version: d.version,
            }),
          ),
        );
        docFails = results.filter((r) => r.status === "rejected").length;
      }

      await deleteFolder(folderRow.id, null);
      qc.invalidateQueries({ queryKey: queryKeys.folders });
      qc.invalidateQueries({ queryKey: ["ihub"] });

      if (docFails === 0) {
        toast.success(`Deleted folder "${name}"`, {
          description: alsoDocs
            ? `Removed ${docs.length} doc${docs.length === 1 ? "" : "s"} too. Drive copies stay.`
            : "Drive folder stays — delete it manually from Drive if you want it gone there too.",
        });
      } else {
        toast.warning(`Folder gone, but ${docFails} docs lingered`, {
          description: "Retry the leftovers from inside the folder.",
        });
      }
      setConfirming(false);
      setAlsoDocs(false);
    } catch (e) {
      toast.error("Couldn't delete folder", {
        description: e instanceof Error ? e.message : "Try again",
      });
    } finally {
      setBusy(false);
    }
  };

  if (confirming) {
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/[0.03] p-5 flex flex-col gap-3">
        <div className="font-medium text-sm">Delete "{name}"?</div>
        {docCount > 0 && (
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
            <Checkbox
              checked={alsoDocs}
              onCheckedChange={(v) => setAlsoDocs(v === true)}
              aria-label="Also delete the docs inside"
              className="h-4 w-4"
            />
            Also delete the {docCount} doc{docCount === 1 ? "" : "s"}{" "}
            inside
          </label>
        )}
        {docCount > 0 && !alsoDocs && (
          <p className="text-[11px] text-muted-foreground">
            Docs will stay filed under the old folder name — they'll
            appear in Library under "{name}" until you re-confirm them
            elsewhere.
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <Button
            size="sm"
            variant="destructive"
            disabled={busy}
            onClick={handleDelete}
            className="h-8"
          >
            {busy ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Yes, delete
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setConfirming(false);
              setAlsoDocs(false);
            }}
            className="h-8"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition">
      <button
        type="button"
        onClick={onOpen}
        className="flex flex-col items-start gap-3 text-left w-full"
      >
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition">
          <FolderOpen className="h-5 w-5" />
        </span>
        <div>
          <div className="font-medium text-sm">{name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {docCount} doc{docCount === 1 ? "" : "s"}
          </div>
        </div>
      </button>
      {/* Delete affordance — visible on hover/focus on desktop, always
          visible on mobile (no hover state to discover). */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setConfirming(true);
        }}
        className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
        aria-label={`Delete folder ${name}`}
        title="Delete folder"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function FiledRow({
  item,
  selected,
  onToggleSelect,
}: {
  item: IhubItem;
  selected: boolean;
  onToggleSelect: (file_id: string) => void;
}) {
  const qc = useQueryClient();
  // Session.email is needed to build a Drive URL that opens with the
  // CORRECT Google account when the user is signed into multiple
  // accounts in Chrome. Without it Drive opens with whatever account
  // is "active" in the user's other tabs and they see "File not found"
  // even though the file exists in the ojaswati Drive.
  const session = useSession();
  const authEmail = session.data?.email ?? null;
  const del = useMutation({
    mutationFn: () =>
      patchItem(item.file_id, {
        event: "USER_DELETE",
        expected_version: item.version,
      }),
    onSuccess: () => {
      // Filed rows live in the "filed" query bucket; invalidate both
      // to refresh the folder grid + the drilled-in list.
      qc.invalidateQueries({ queryKey: ["ihub"] });
      toast.success(`Deleted "${item.name}"`, {
        description:
          "The Drive copy stays — delete it manually from Drive if you want it gone there too.",
      });
    },
    onError: (e) => {
      toast.error("Couldn't delete", {
        description: e instanceof Error ? e.message : "Try again shortly",
      });
    },
  });

  // Two-tap inline delete confirm (window.confirm is unreliable in
  // iOS standalone PWAs). First tap morphs the trash button into
  // "✓ confirm · cancel"; second tap commits.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  useEffect(() => {
    if (!confirmingDelete) return;
    const t = setTimeout(() => setConfirmingDelete(false), 5000);
    return () => clearTimeout(t);
  }, [confirmingDelete]);

  // Inline rename. Pencil opens an input. Save calls PATCH
  // /api/ihub/<id>/rename which also renames the Drive file.
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(item.name);
  const rename = useMutation({
    mutationFn: (newName: string) => renameItem(item.file_id, newName),
    onSuccess: (res) => {
      setRenaming(false);
      qc.invalidateQueries({ queryKey: ["ihub"] });
      toast.success(`Renamed to "${res.item.name}"`, {
        description: res.drive_renamed
          ? "Drive file renamed too."
          : "Drive file kept its old name — re-sign in to Drive and try again if needed.",
      });
    },
    onError: (e) => {
      toast.error("Rename failed", {
        description: e instanceof Error ? e.message : "Try again",
      });
    },
  });

  return (
    <li
      className={[
        "flex items-center gap-3 rounded-lg border bg-card p-3 hover:shadow-sm transition",
        selected ? "border-primary/60 ring-2 ring-primary/30 bg-primary/[0.03]" : "border-border",
      ].join(" ")}
    >
      <label
        className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground shrink-0 cursor-pointer hover:bg-muted/70 transition relative group"
        onClick={(e) => e.stopPropagation()}
        aria-label={`Select ${item.name}`}
      >
        {selected ? (
          <Checkbox
            checked
            onCheckedChange={() => onToggleSelect(item.file_id)}
            aria-label="Selected"
            className="h-4 w-4"
          />
        ) : (
          <>
            <FileText className="h-4 w-4 group-hover:opacity-30 transition" />
            <Checkbox
              checked={false}
              onCheckedChange={() => onToggleSelect(item.file_id)}
              aria-label="Select"
              className="h-4 w-4 absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition"
            />
          </>
        )}
      </label>
      <div className="flex-1 min-w-0">
        {renaming ? (
          <div className="flex items-center gap-1.5">
            <Input
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              spellCheck={false}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (renameDraft.trim() && renameDraft.trim() !== item.name) {
                    rename.mutate(renameDraft.trim());
                  }
                } else if (e.key === "Escape") {
                  setRenaming(false);
                  setRenameDraft(item.name);
                }
              }}
            />
            <Button
              size="sm"
              className="h-8 w-8 p-0"
              disabled={
                rename.isPending ||
                !renameDraft.trim() ||
                renameDraft.trim() === item.name
              }
              onClick={() => rename.mutate(renameDraft.trim())}
              aria-label="Save new name"
            >
              {rename.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => {
                setRenaming(false);
                setRenameDraft(item.name);
              }}
              aria-label="Cancel rename"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <>
            <div className="text-sm font-medium truncate">{item.name}</div>
            <div className="text-xs text-muted-foreground">
              Filed {timeAgo(item.state_changed_at)}
            </div>
          </>
        )}
      </div>
      {!renaming && (
        <>
          {item.drive_file_id ? (
            <a
              href={buildDriveUrl(item.drive_file_id, authEmail)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              title={
                authEmail
                  ? `Opens Drive as ${authEmail}`
                  : "Opens Drive in a new tab"
              }
            >
              Open in Drive <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground shrink-0 h-8 w-8 p-0"
            onClick={() => {
              setRenameDraft(item.name);
              setRenaming(true);
            }}
            aria-label={`Rename ${item.name}`}
            title="Rename file (also renames in Drive)"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {confirmingDelete ? (
            <>
              <Button
                size="sm"
                variant="destructive"
                className="shrink-0 h-8"
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                {del.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Confirm
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="shrink-0 h-8"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive shrink-0 h-8 w-8 p-0"
              onClick={() => setConfirmingDelete(true)}
              aria-label={`Delete ${item.name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </>
      )}
    </li>
  );
}

function timeAgo(unixSec: number): string {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - unixSec);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
